import { useEffect, useState, createContext } from "react";
import toast, { Toaster } from "react-hot-toast";
import dayjs from "dayjs";
import { post, get, put, uploadFile } from "../utils/api";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import localizedFormat from "dayjs/plugin/localizedFormat";
import relativeTime from "dayjs/plugin/relativeTime";
import PropTypes from "prop-types";

// Extend dayjs with plugins
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(localizedFormat);
dayjs.extend(relativeTime);

export const AuthContext = createContext();

export default function AuthContextProvider({ children }) {
  AuthContextProvider.propTypes = {
    children: PropTypes.any.isRequired,
  };

  const [isAuth, SetAuth] = useState(() => {
    const storedIsAuth = localStorage.getItem("isAuth");
    return storedIsAuth ? JSON.parse(storedIsAuth) : false;
  });

  const setAuthAndPersist = (value) => {
    SetAuth(value);
    localStorage.setItem("isAuth", JSON.stringify(value));
  };

  const [loginUser, setLoginUser] = useState(() => {
    const storedLoginUser = localStorage.getItem("loginUser");
    return storedLoginUser ? JSON.parse(storedLoginUser) : {};
  });

  const setLoginUserAndPersist = (user) => {
    setLoginUser(user);
    localStorage.setItem("loginUser", JSON.stringify(user));
  };

  const [domainUnderConsideration, setDomainUnderConsideration] = useState(
    () => {
      const storedDomainUnderConsideration = localStorage.getItem(
        "domainUnderConsideration"
      );
      return (
        storedDomainUnderConsideration ||
        JSON.parse(storedDomainUnderConsideration) ||
        null
      );
    }
  );

  const setDomainUnderConsiderationAndPersist = (domainId) => {
    setDomainUnderConsideration(domainId);
    localStorage.setItem("domainUnderConsideration", JSON.stringify(domainId));
  };

  const [tickets, setTickets] = useState([]);
  const [fetchingTickets, setFetchingTickets] = useState(false);

  // ==================== 🆕 SUBMISSION TRACKING STATE ====================
  const [userSubmissions, setUserSubmissions] = useState([]);
  const [submissionStats, setSubmissionStats] = useState(null);
  const [loadingSubmissions, setLoadingSubmissions] = useState(false);

  const resetState = () => {
    SetAuth(false);
    setLoginUser({});
    setUserSubmissions([]);
    setSubmissionStats(null);
    localStorage.setItem("isAuth", JSON.stringify(false));
    localStorage.removeItem("loginUser");
    // Tokens are in httpOnly cookies, managed by server
  };

  let loginme = async (data) => {
    try {
      // ✅ Use secure API client with httpOnly cookies
      const result = await post("/sam/doc/users/authenticate", data);

      if (result.success) {
        // ✅ Tokens are automatically stored in httpOnly cookies by the server
        // NO localStorage.setItem("token") needed!

        // Save user data only
        const user = result.user;
        setLoginUser(user);
        SetAuth(true);
        localStorage.setItem("isAuth", JSON.stringify(true));
        localStorage.setItem("loginUser", JSON.stringify(user));
        toast.success(`Welcome back, ${user.email_id}`);

        // Fetch user data
        if (user._id) {
          getTicket(user._id);
          fetchUserSubmissions(user._id);
        }

        // ✅ No setTimeout needed - server manages token expiration
      } else {
        toast.error(result.error || "Login failed");
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "An error occurred while logging in";
      toast.error(errorMessage);
    }
  };

  // Adding useEffect to listen to storage events
  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === "isAuth") {
        const isAuth = JSON.parse(event.newValue);
        SetAuth(isAuth);
      }
      if (event.key === "loginUser") {
        const user = JSON.parse(event.newValue);
        setLoginUser(user);
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  let logoutme = async (data) => {
    try {
      // ✅ Use secure API client - server will clear httpOnly cookies
      await post("/sam/doc/users/logout", data);
      resetState();
      toast.success("Logged out successfully. See you soon!");
    } catch (error) {
      // Clear local state even if API call fails
      resetState();
      toast.success("Logged out successfully. See you soon!");
    }
  };

  const updateLoginUser = (updatedUser) => {
    setLoginUser(updatedUser);
    localStorage.setItem("loginUser", JSON.stringify(updatedUser));
  };

  const formatDate = (dateString) => {
    const date = dayjs(dateString);
    const twoDaysAgo = dayjs().subtract(2, "day");

    if (date.isAfter(twoDaysAgo)) {
      // Less than 2 days ago, show relative time
      return date.fromNow();
    } else {
      // More than 2 days ago, show formatted date
      return date.format("D MMM, YYYY, hh:mm A [IST]");
    }
  };

  const updateDescriptionSpecialities = async (
    userId,
    description,
    specialities
  ) => {
    try {
      const response = await fetch(
        `https://domains-marketplace-backend.vercel.app/sam/doc/users/${userId}/update-description-specialities`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ description, specialities }),
        }
      );

      if (!response.ok) {
        throw new Error(response.statusText);
      }

      const updatedUser = await response.json();
      updateLoginUser(updatedUser.user);
      toast.success("Description and specialities updated successfully");
    } catch (error) {
      toast.error("Failed to update details");
    }
  };

  const addSubscriber = async (email_id) => {
    try {
      const response = await fetch(
        "https://domains-marketplace-backend.vercel.app/subscribers/addSubscriber",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email_id }),
        }
      );

      if (!response.ok) {
        throw new Error("Subscription failed: " + response.statusText);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      throw error;
    }
  };

  // ==================== ✅ SECURE addProduct with httpOnly Cookies ====================
  const addProduct = async (productData) => {
    try {
      // ✅ No token check needed - httpOnly cookie sent automatically

      // ✅ Add user information to FormData if not already present
      if (loginUser._id && !productData.has("userId")) {
        productData.append("userId", loginUser._id);
      }
      if (loginUser.email_id && !productData.has("email")) {
        productData.append("email", loginUser.email_id);
      }
      if (loginUser.username && !productData.has("ownerName")) {
        productData.append("ownerName", loginUser.username);
      }

      const result = await uploadFile("/domain/addproduct", productData);

      if (result.success) {
        toast.success("Domain submitted successfully! Awaiting admin approval.");

        // ✅ Refresh user submissions after successful submit
        if (loginUser._id) {
          await fetchUserSubmissions(loginUser._id);
        }

        return result;
      } else {
        const errorMessage = result.message || result.error || "Failed to submit domain";
        toast.error(errorMessage);
        return { success: false, message: errorMessage };
      }
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      toast.error(`Failed to create product: ${errorMessage}`);

      return { success: false, message: errorMessage };
    }
  };

  // ==================== 🆕 FETCH USER SUBMISSIONS ====================
  const fetchUserSubmissions = async (userId) => {
    if (!userId) {
      return;
    }

    setLoadingSubmissions(true);
    try {
      const result = await get(`/domain/mysubmissions/${userId}`);

      if (result.success) {
        setUserSubmissions(result.submissions || []);
        setSubmissionStats(result.stats || null);
      }
    } catch (error) {
      // Don't show error toast on every fetch
    } finally {
      setLoadingSubmissions(false);
    }
  };

  // ==================== 🆕 REFRESH PRODUCTS ====================
  const refreshProducts = async () => {
    if (loginUser._id) {
      await fetchUserSubmissions(loginUser._id);
    }
  };

  // ==================== 🆕 GET DASHBOARD STATS ====================
  const fetchDashboardStats = async (userId) => {
    try {
      const response = await fetch(
        `https://domains-marketplace-backend.vercel.app/domain/dashboard-stats/${userId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch dashboard stats");
      }

      const result = await response.json();
      return result;
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const raiseTicket = async (productData) => {
    try {
      const response = await fetch(
        "https://domains-marketplace-backend.vercel.app/tickets/raise",
        {
          method: "POST",
          body: productData, // Send FormData directly
        }
      );

      const data = await response.json();

      if (response.ok) {
        toast.success("Ticket raised successfully!");
        setTickets((prevTickets) => [...prevTickets, data.ticket]);
        return data;
      } else {
        if (data.details && data.details.length > 0) {
          toast.error(`Validation error: ${data.details.join(", ")}`);
        } else {
          toast.error(`Error: ${data.message}`);
        }
      }
    } catch (error) {
      toast.error(`Failed to create a ticket: ${error.message}`);
    }
  };

  const getTicket = async (userId) => {
    setFetchingTickets(true);
    try {
      const response = await fetch(
        `https://domains-marketplace-backend.vercel.app/tickets/${userId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Tickets retrieval failed: " + response.statusText);
      }

      const result = await response.json();
      setTickets(result);
      setFetchingTickets(false);
      return result;
    } catch (error) {
      setFetchingTickets(false);
    }
  };

  const updateUserData = async (userId, firstName, lastName, mobileNumber) => {
    try {
      // ✅ Use secure API client with httpOnly cookies
      const result = await put(`/sam/doc/users/update/${userId}`, {
        firstName,
        lastName,
        mobile_number: mobileNumber,
      });

      const updatedUser = result.user;

      if (updatedUser) {
        setLoginUserAndPersist(updatedUser); // ✅ Update context + localStorage
        toast.success("Profile updated successfully!");
      } else {
        toast.error("Unexpected server response.");
      }
    } catch (error) {
      const errorMessage = error.response?.data?.error || "Failed to update profile.";
      toast.error(errorMessage);
    }
  };

  const replyTicket = async (ticketId, replyData) => {
    try {
      const response = await fetch(
        `https://domains-marketplace-backend.vercel.app/tickets/${ticketId}/reply`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(replyData),
        }
      );

      if (response.ok) {
        const result = await response.json();
        const { updatedTicket } = result;

        setTickets((prevTickets) =>
          prevTickets.map((ticket) =>
            ticket._id === updatedTicket._id ? updatedTicket : ticket
          )
        );

        toast.success("Ticket replied successfully!");
      } else {
        const errorData = await response.json();
        toast.error(`Error: ${errorData.message}`);
      }
    } catch (error) {
      toast.error("Failed to reply for the ticket.");
    }
  };

  const reOpenTicket = async (ticketId, reOpenData) => {
    try {
      const response = await fetch(
        `https://domains-marketplace-backend.vercel.app/tickets/${ticketId}/re-open`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(reOpenData),
        }
      );

      if (response.ok) {
        const result = await response.json();
        const { updatedTicket } = result;

        setTickets((prevTickets) =>
          prevTickets.map((ticket) =>
            ticket._id === updatedTicket._id ? updatedTicket : ticket
          )
        );

        toast.success("Ticket re-opened successfully!");
      } else {
        const errorData = await response.json();
        toast.error(`Error: ${errorData.message}`);
      }
    } catch (error) {
      toast.error("Failed to re-open for the ticket.");
    }
  };

  const feedbackTicket = async (ticketId, feedbackData) => {
    try {
      const response = await fetch(
        `https://domains-marketplace-backend.vercel.app/tickets/${ticketId}/feedback`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(feedbackData),
        }
      );

      if (response.ok) {
        const result = await response.json();
        const { updatedTicket } = result;

        setTickets((prevTickets) =>
          prevTickets.map((ticket) =>
            ticket._id === updatedTicket._id ? updatedTicket : ticket
          )
        );

        toast.success("Feedback provided successfully!");
      } else {
        const errorData = await response.json();
        toast.error(`Error: ${errorData.message}`);
      }
    } catch (error) {
      toast.error("Failed to provide feedback for the ticket.");
    }
  };

  const payMembershipFee = async (data) => {
    try {
      const response = await fetch(
        "https://domains-marketplace-backend.vercel.app/sam/doc/users/pay-membership-fee",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(data),
        }
      );
      if (response.ok) {
        const result = await response.json();
        setLoginUserAndPersist(result);
        toast.success("Payment Successful!");
      } else {
        toast.error("Payment Failed.");
      }
    } catch (error) {
      toast.error("Payment Failed.");
    }
  };

  // ✅ Fetch tickets and submissions on mount
  useEffect(() => {
    if (loginUser._id) {
      getTicket(loginUser._id);
      fetchUserSubmissions(loginUser._id);
    }
  }, [loginUser._id]);

  return (
    <AuthContext.Provider
      value={{
        isAuth,
        loginme,
        logoutme,
        loginUser,
        updateLoginUser,
        updateUserData,
        formatDate,
        updateDescriptionSpecialities,
        addSubscriber,
        raiseTicket,
        getTicket,
        addProduct,
        tickets,
        replyTicket,
        reOpenTicket,
        feedbackTicket,
        fetchingTickets,
        payMembershipFee,
        setAuthAndPersist,
        setLoginUserAndPersist,
        domainUnderConsideration,
        setDomainUnderConsiderationAndPersist,
        // ✅ NEW: Submission tracking
        userSubmissions,
        submissionStats,
        loadingSubmissions,
        fetchUserSubmissions,
        refreshProducts,
        fetchDashboardStats,
      }}
    >
      {children}
      <Toaster />
    </AuthContext.Provider>
  );
}
